
import { UserSummary } from "@/api/dtos/Manager.schema";
import { User } from "@/api/dtos/User.schema";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import CancelButton from "../buttons/CancelButton.vue";
import DangerButton from "../buttons/DangerButton.vue";
import AddButton from "../buttons/AddButton.vue";
import EditFrame from "../EditFrame.vue";
import AddPersonDialog from "@/components/dialogs/AddPersonDialog.vue";
import DangerDialog from "@/components/dialogs/DangerDialog.vue";
import moment from "moment";
import { Operator } from "@/api/dtos/Operator.schema";
import api from "@/api";
import { OperatorsModule } from "@/store/modules/OperatorsModule";
import { deepClone } from "@/misc/helpers";
import { UsersModule } from "@/store/modules/UsersModule";
import { PendingInvitation } from "@/api/dtos/Operator.schema";

import { showToast } from "@/utils/toastService";
import { getAxiosResponse } from "@/utils/errorUtils";

@Component({
  components: {
    EditFrame,
    CancelButton,
    DangerButton,
    AddButton,
    AddPersonDialog,
    DangerDialog,
  },
})
export default class Owners extends Vue {
  moment = moment;

  @Prop()
  operator!: Operator;

  editedOwners: UserSummary[] = [];
  dialog = false;
  showDeleteOwnerDialog = false;
  ownerToDelete: UserSummary | null = null;

  readonly headers = [
    { text: "Email", value: "email", align: "center" },
    { text: "Status", value: "status", align: "center" },
    { text: "Letzter Login", value: "lastLogin", align: "center" },
    { text: "", value: "actions", align: "center" },
  ];

  mounted(): void {
    this.backupData();
    this.editedOwners = deepClone(this.operator.owners);
  }

  get tableOwners(): Array<any> {
    const active = (this.operator.owners ?? []).map(o => ({
      kind: "ACTIVE",
      email: o.email,
      lastLogin: o.lastLogin,
      userUuid: o.userUuid,
      status: "Aktiv",
    }));

    const pending = (this.operator.pendingOwners ?? []).map((p: PendingInvitation) => ({
      kind: "PENDING",
      email: p.email,
      lastLogin: null,
      userUuid: null,
      status: p.status === "EXPIRED" ? "Token abgelaufen" : "Registrierung ausstehend",
    }));

    return [...active, ...pending].sort((a,b) => a.kind.localeCompare(b.kind));
  }

  get localUser(): User {
    return UsersModule.localUser!;
  }

  async onAddOwner(email: string): Promise<void> {
    try {
      await api.OperatorsApi.addOwner(this.operator.uuid, email);
      showToast({
        message: `Eigentümer erfolgreich hinzugefügt`,
        type: "success",
      });
      OperatorsModule.refreshDetailedOperator(); // this is pretty inefficient, but it works
    } catch (e) {
      showToast({
        response: getAxiosResponse(e),
        message: `Fehler beim Hinzufügen des Eigentümers`,
        type: "error",
      });
      console.error("Failed to add owner:", e);
    }
  }

  async onResendInvite(email: string): Promise<void> {
    try {
      await api.OperatorsApi.resendOwnerInvite(this.operator.uuid, email);
      showToast({ message: "Einladung erneut gesendet", type: "success" });
      OperatorsModule.refreshDetailedOperator();
    } catch (e) {
      showToast({
        response: getAxiosResponse(e),
        message: "Fehler beim erneuten Senden",
        type: "error",
      });
    }
  }

  async onDeleteOwner(): Promise<void> {
    try {
      if (this.ownerToDelete === null) return;
      await api.OperatorsApi.deleteOwner(
        this.operator.uuid,
        this.ownerToDelete.userUuid
      );
      showToast({
        message: `Eigentümer erfolgreich entfernt`,
        type: "success",
      });

      OperatorsModule.refreshDetailedOperator(); // this is pretty inefficient, but it works
      this.ownerToDelete = null;
    } catch (e) {
      showToast({
        response: getAxiosResponse(e),
        message: `Fehler beim Entfernen des Eigentümers`,
        type: "error",
      });
      console.error("Failed to remove owner:", e);
    }
  }

  @Watch("operator")
  backupData(): void {
    this.editedOwners = deepClone(this.operator.owners);
  }

  onDeleteOwnerClick(owner: any): void {
    if (owner.kind === "PENDING") return;
    this.showDeleteOwnerDialog = true;
    this.ownerToDelete = owner;
  }
}
